import { useEffect, useState } from 'react';
import React from 'react';
import axios from 'axios';
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    CardTitle,
    Col,
    Label,
    Row,
} from 'reactstrap';
import UserAuthKey from '../../../components/Common/UserAuthKey';
import toast from 'react-hot-toast'; // Import toast for error notifications
import { FINANCE_ROLE_ID, FINANCE_MANAGER_ROLE_ID, AUDIT_ROLE_ID, SHOW_ROOM_MANAGER_ROLE_ID, CUSTOMER_CARE_MANAGER_ROLE_ID, CUSTOMER_CARE_SUPERVISOR_ROLE_ID, STORE_CUSTOMER_CARE_SUPERVISOR_ROLE_ID } from '../../../constants/roles'; // Import role constants
import './BankInfo.css';

const BankInfo = (props) => {
    const { refundData, editMode, onClose: onDrawerClose, fetchRefundList, paginationValue, limitValue, sortingValue, searchValue, customActiveTab, currentCountry, advancedFilter, stores, onUpdateRefundListTrigger } = props;
    const [status, setStatus] = React.useState(refundData?.Status || '0');
    const [remark, setRemark] = React.useState(refundData?.Remark || '');
    const [loading, setLoading] = React.useState(false);
    const [activityLog, setActivityLog] = useState([]);
    const [refundReference, setRefundReference] = useState(''); // Add state for refund reference
    const [selectedOption, setSelectedOption] = React.useState(''); // Add state for selected option
    const [rejectedReason, setRejectedReason] = useState(''); // Add state for rejected reason
    const [paymentProofFiles, setPaymentProofFiles] = useState([]); // [{ id, file_name, extension, file: base64 }]
    const paymentProofInputRef = React.useRef(null);
    const bearerToken = UserAuthKey();
    let userID = '';
    let UserRoleID = '';
    if (localStorage.getItem('authUser')) {
        const userObj = JSON.parse(localStorage.getItem('authUser'));
        if (userObj?.role_id !== undefined) {
            userID = userObj?.user_id;
            UserRoleID = userObj?.role_id;
        }
    }
    const isFinanceRole = FINANCE_ROLE_ID.includes(UserRoleID);
    // ID proofs are downloadable by finance, finance manager and audit
    const canDownloadIdProof =
        isFinanceRole ||
        FINANCE_MANAGER_ROLE_ID.includes(UserRoleID) ||
        AUDIT_ROLE_ID.includes(UserRoleID);
    // Payment proof is downloadable by finance, finance manager and audit
    const canDownloadPaymentProof =
        FINANCE_ROLE_ID.includes(UserRoleID) ||
        FINANCE_MANAGER_ROLE_ID.includes(UserRoleID) ||
        AUDIT_ROLE_ID.includes(UserRoleID);
    const isFinanceApprovalPendingStatus = refundData?.Status === '9' || refundData?.Status === 9;
    const getPaymentProofZipUrl = (attachment) => {
        const isZipUrl = (value) => {
            if (typeof value !== 'string') return false;
            const trimmed = value.trim();
            if (!trimmed) return false;
            const normalized = trimmed.toLowerCase();
            if (normalized.includes('.zip') || /zip([?#]|$)/i.test(normalized)) return true;
            try {
                const decoded = decodeURIComponent(trimmed).toLowerCase();
                return decoded.includes('.zip') || /zip([?#]|$)/i.test(decoded);
            } catch (e) {
                return false;
            }
        };

        const extractZip = (value, depth = 0) => {
            if (!value || depth > 4) return '';

            if (Array.isArray(value)) {
                for (const item of value) {
                    const found = extractZip(item, depth + 1);
                    if (found) return found;
                }
                return '';
            }

            if (typeof value === 'string') {
                const trimmed = value.trim();
                if (!trimmed) return '';

                if (isZipUrl(trimmed)) return trimmed;

                if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
                    try {
                        const parsed = JSON.parse(trimmed);
                        return extractZip(parsed, depth + 1);
                    } catch (e) {
                    }
                }

                return '';
            }

            if (typeof value === 'object') {
                const directKeys = [
                    'zip',
                    'zipUrl',
                    'zip_url',
                    'payment_proof_zip',
                    'paymentProofZip',
                    'paymentProofZipUrl',
                    'PaymentProofZipUrl',
                    'payment_proof_attachment',
                    'PaymentProofAttachment',
                    'paymentProofAttachment',
                    'attachment',
                    'url',
                    'file',
                ];

                for (const key of directKeys) {
                    const found = extractZip(value?.[key], depth + 1);
                    if (found) return found;
                }

                for (const [key, item] of Object.entries(value)) {
                    if (typeof item === 'string' && key.toLowerCase().includes('zip') && isZipUrl(item)) {
                        return item;
                    }
                }

                for (const item of Object.values(value)) {
                    const found = extractZip(item, depth + 1);
                    if (found) return found;
                }
            }

            return '';
        };

        return extractZip(attachment);
    };
    const paymentProofZipUrl = getPaymentProofZipUrl(
        [
            refundData?.PaymentProofAttachment,
            refundData?.paymentProofAttachment,
            refundData?.attachment,
            refundData?.payment_proof,
            refundData?.PaymentProof,
            refundData?.paymentProof,
            refundData?.payment_proof_attachment,
            refundData?.payment_proof_zip,
            refundData?.paymentProofZip,
            refundData?.paymentProofZipUrl,
            refundData?.PaymentProofZipUrl,
        ]
    );
    const idFrontImage = refundData?.idFront || refundData?.id_front || refundData?.IdFront || '';
    const idBackImage = refundData?.idBack || refundData?.id_back || refundData?.IdBack || '';
    const getPaymentProofBase64 = (proof) => {
        if (!proof) return '';

        if (Array.isArray(proof)) {
            return proof.find((item) => typeof item === 'string' && item && !item.toLowerCase().includes('.zip')) || '';
        }

        if (typeof proof === 'string') {
            try {
                const parsed = JSON.parse(proof);
                if (Array.isArray(parsed)) {
                    return parsed.find((item) => typeof item === 'string' && item && !item.toLowerCase().includes('.zip')) || '';
                }
                if (parsed && typeof parsed === 'object') {
                    return parsed.base64 || parsed.file || parsed.url || parsed.payment_proof || parsed.paymentProof || '';
                }
            } catch (e) {
            }

            if (proof.toLowerCase().includes('.zip')) return '';
            return proof;
        }

        if (typeof proof === 'object') {
            return proof.base64 || proof.file || proof.url || proof.payment_proof || proof.paymentProof || '';
        }

        return '';
    };
    const paymentProofBase64 = getPaymentProofBase64(
        refundData?.payment_proof ||
        refundData?.PaymentProof ||
        refundData?.paymentProof ||
        refundData?.PaymentProofAttachment ||
        refundData?.paymentProofAttachment ||
        refundData?.attachment
    );
    const paymentProofDownloadUrl = paymentProofBase64
        ? paymentProofBase64.startsWith('data:')
            ? paymentProofBase64
            : paymentProofBase64.startsWith('http') || paymentProofBase64.startsWith('/') || paymentProofBase64.startsWith('blob:')
                ? paymentProofBase64
            : `data:application/octet-stream;base64,${paymentProofBase64}`
        : '';
    const paymentProofUrl = paymentProofZipUrl || paymentProofDownloadUrl;
    // Fetch activity log on mount
    useEffect(() => {
        if (!refundData?.id) return;
        axios
            .get(`${process.env.REACT_APP_OMS_API}v1/activitylog/list`, {
                params: {
                    activity_id: refundData.id,
                    activity_name: 'bank-refund',
                },
                headers: {
                    Authorization: `Bearer ${bearerToken}`,
                },
            })
            .then((res) => {
                if (res.data && Array.isArray(res.data.data)) {
                    setActivityLog(res.data.data);
                }
            })
            .catch((err) => {
                // Optionally handle error
            });
        // eslint-disable-next-line
    }, [refundData?.id]);
    //----------------------------------------
    //               STATUSES
    //----------------------------------------
    //0 Pending 
    //2 Approved
    //3 completed
    //5 Rejected
    //6 closed
    //7 pendng cs manager
    //8 pendng show room mamnage
    //9 finance approval pendng
    //10 Finance approved
    //11 Finance Rejected
    //20 Audit approval pending
    //12 Show room manager approved
    //13 Show room manager rejected
    //14 Customer service manager approved
    //15 Customer service manager rejected

    const statusOptions = [       
        { value: '2', label: 'Approve' },
        { value: '3', label: 'Completed' },
        { value: '4', label: 'Clarification Required' }, // Added new status
        { value: '5', label: 'Rejected' }, // Added new status
        { value: '6', label: 'Closed' }, // Added new status

    ];

    const ALLOWED_PAYMENT_PROOF_TYPES = ['application/pdf', 'image/jpeg'];
    const ALLOWED_PAYMENT_PROOF_EXTENSIONS = ['pdf', 'jpg', 'jpeg'];
    const PAYMENT_PROOF_EXTENSION_BY_TYPE = { 'application/pdf': 'pdf', 'image/jpeg': 'jpg' };
    const PAYMENT_PROOF_MIME_BY_EXTENSION = { pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg' };

    function showPaymentProofError(message) {
        toast.error(message, {
            style: {
                border: '1px solid #fff',
                padding: '20px',
                color: '#ffffff',
                background: '#f44336',
            },
            iconTheme: {
                primary: '#FFFAEE',
                secondary: '#f44336',
            },
        });
    }

    function readFileAsBase64(file) {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (event) => resolve(String(event.target.result).split(',')[1] || '');
            reader.onerror = () => resolve('');
            reader.readAsDataURL(file);
        });
    }

    async function handlePaymentProofChange(e) {
        // Copy the list out of the input first: the FileList is cleared once the input is reset
        const selectedFiles = Array.from(e.target.files || []);
        if (!selectedFiles.length) return;

        const allowedFiles = [];
        const rejectedFileNames = [];
        selectedFiles.forEach((file) => {
            const extension = file.name.split('.').pop()?.toLowerCase() || '';
            const isAllowed =
                ALLOWED_PAYMENT_PROOF_TYPES.includes(file.type) ||
                ALLOWED_PAYMENT_PROOF_EXTENSIONS.includes(extension);
            if (isAllowed) {
                allowedFiles.push({ file, extension });
            } else {
                rejectedFileNames.push(file.name);
            }
        });

        if (rejectedFileNames.length) {
            showPaymentProofError(
                'Only PDF, JPG and JPEG files are allowed for Payment Proof. Skipped: ' + rejectedFileNames.join(', ')
            );
        }
        if (!allowedFiles.length) return;

        const newProofs = await Promise.all(
            allowedFiles.map(async ({ file, extension }) => ({
                // Name alone is not unique - the same name can be picked from two folders
                id: `${file.name}-${file.size}-${file.lastModified}`,
                file_name: file.name,
                // Keep the uploaded file's own extension so a JPG stays a .jpg (and a PDF a .pdf) inside the export zip
                extension: ALLOWED_PAYMENT_PROOF_EXTENSIONS.includes(extension)
                    ? extension
                    : PAYMENT_PROOF_EXTENSION_BY_TYPE[file.type] || '',
                file: await readFileAsBase64(file),
            }))
        );

        // Never drop a file silently - tell the user which ones could not be read
        const unreadableProofs = newProofs.filter((proof) => !proof.file);
        if (unreadableProofs.length) {
            showPaymentProofError(
                'Could not read: ' + unreadableProofs.map((proof) => proof.file_name).join(', ') + '. Please try again.'
            );
        }

        setPaymentProofFiles((previousProofs) => {
            const mergedProofs = [...previousProofs];
            newProofs
                .filter((proof) => proof.file)
                .forEach((proof) => {
                    if (!mergedProofs.some((existing) => existing.id === proof.id)) {
                        mergedProofs.push(proof);
                    }
                });
            return mergedProofs;
        });

        // Reset only after every file has been read, so the same file can be picked again once removed
        if (paymentProofInputRef.current) {
            paymentProofInputRef.current.value = '';
        }
    }

    function handleRemovePaymentProof(proofId) {
        setPaymentProofFiles((previousProofs) => previousProofs.filter((proof) => proof.id !== proofId));
    }

    // Saved proofs come back either as a list or as the single base64 field the older API used
    function getSavedPaymentProofs() {
        const savedProofs = refundData?.paymentProofs || refundData?.payment_proofs;
        const proofList =
            Array.isArray(savedProofs) && savedProofs.length
                ? savedProofs
                : [
                      {
                          file_name: refundData?.paymentProofName || refundData?.payment_proof_name,
                          extension: refundData?.paymentProofExtension || refundData?.payment_proof_extension,
                          file: refundData?.paymentProof || refundData?.payment_proof,
                      },
                  ];

        return proofList
            .filter((proof) => proof?.url || proof?.file)
            .map((proof, index) => {
                const source = proof.url || proof.file;
                const extension = (
                    proof.extension ||
                    proof.file_name?.split('.').pop() ||
                    'jpg'
                ).toLowerCase();
                const isLink = /^(https?:|data:)/i.test(source);
                return {
                    fileName: proof.file_name || `Payment_Proof_${index + 1}.${extension}`,
                    href: isLink
                        ? source
                        : `data:${PAYMENT_PROOF_MIME_BY_EXTENSION[extension] || 'application/octet-stream'};base64,${source}`,
                };
            });
    }

    // Helper to get user_id from localStorage
    function getUserId() {
        if (localStorage.getItem('authUser')) {
            const userObj = JSON.parse(localStorage.getItem('authUser'));
            if (userObj?.role_id !== undefined) {
                return userObj?.user_id;
            }
        }
        return '';
    }

    async function handleUpdate() {
        let updatedStatus = status; // Use a new variable to avoid reassigning a constant
        let requestSource = null; // Default value for requestSource

        if (updatedStatus === '3' && !refundReference) {
            toast.error('Refund reference is mandatory when status is completed.', {
                style: {
                    border: '1px solid #fff',
                    padding: '20px',
                    color: '#ffffff',
                    background: '#f44336',
                },
                iconTheme: {
                    primary: '#FFFAEE',
                    secondary: '#f44336',
                },
            });
            return;
        }

        // Mandatory rejected reason for finance role and rejected status
        if (
            updatedStatus === '5' &&
            (FINANCE_ROLE_ID.includes(UserRoleID) || FINANCE_MANAGER_ROLE_ID.includes(UserRoleID)) &&
            !rejectedReason
        ) {
            toast.error('Rejected Reason is mandatory for Finance when status is Rejected.', {
                style: {
                    border: '1px solid #fff',
                    padding: '20px',
                    color: '#ffffff',
                    background: '#f44336',
                },
                iconTheme: {
                    primary: '#FFFAEE',
                    secondary: '#f44336',
                },
            });
            return;
        }

        if (updatedStatus === '2' && FINANCE_ROLE_ID.includes(UserRoleID) && refundData?.Status === '9') {
            updatedStatus = '10';
            requestSource = 'finance';
        }

        if (isFinanceApprovalPendingStatus && !paymentProofFiles.length) {
            toast.error('Payment Proof is mandatory for Finance Approval Pending status.', {
                style: {
                    border: '1px solid #fff',
                    padding: '20px',
                    color: '#ffffff',
                    background: '#f44336',
                },
                iconTheme: {
                    primary: '#FFFAEE',
                    secondary: '#f44336',
                },
            });
            return;
        }

        if (updatedStatus === '5' && FINANCE_ROLE_ID.includes(UserRoleID) && refundData?.Status === '9') {
            updatedStatus = '11';
            requestSource = 'finance';
        }
        //Finance manager Approved - moves a finance approved refund (10) forward
        if (updatedStatus === '2' && FINANCE_MANAGER_ROLE_ID.includes(UserRoleID) && refundData?.Status === '10') {
            updatedStatus = '20';
            requestSource = 'finance';
        }
        //Finance manager Reject
        if (updatedStatus === '5' && FINANCE_MANAGER_ROLE_ID.includes(UserRoleID) && refundData?.Status === '10') {
            updatedStatus = '11';
            requestSource = 'finance';
        }
        //Show room  manager Approved
        if (updatedStatus === '2' && UserRoleID === SHOW_ROOM_MANAGER_ROLE_ID) {
            updatedStatus = '9';
            requestSource = 'show-room-manager';
        }
        //Show room  manager Reject
        if (updatedStatus === '5' && UserRoleID === SHOW_ROOM_MANAGER_ROLE_ID) {
            updatedStatus = '13';
            requestSource = 'show-room-manager';
        }
        //CS manager Approved
        if (updatedStatus === '2' && UserRoleID === CUSTOMER_CARE_MANAGER_ROLE_ID) {
            updatedStatus = '9';
            requestSource = 'customer-service-manager';
        }
        //CS manager Reject
        if (updatedStatus === '5' && UserRoleID === CUSTOMER_CARE_MANAGER_ROLE_ID) {
            updatedStatus = '15';
            requestSource = 'customer-service-manager';
        }

        setLoading(true);
        const user_id = getUserId();

        try {
            const response = await axios({
                method: 'POST',
                url: `${process.env.REACT_APP_OMS_API}v1/orders/update-refund-bank`,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${bearerToken}`,
                },
                data: {
                    status: updatedStatus, // Use the updated status here
                    id: refundData?.id,
                    user_id,
                    event_id: 'bank-refund',
                    comment: remark,
                    refund_reference: refundReference,
                    request_source: requestSource, // Include the request source in the request
                    rejected_reason: rejectedReason, // Pass rejected reason
                    payment_proof: paymentProofFiles[0]?.file || '', // Base64 of the first proof, kept for the existing API contract
                    payment_proof_name: paymentProofFiles[0]?.file_name || '', // Original file name
                    payment_proof_extension: paymentProofFiles[0]?.extension || '', // pdf | jpg | jpeg, used when saving the file in the zip
                    // All uploaded proofs, without the internal id: [{ file_name, extension, file }]
                    payment_proofs: paymentProofFiles.map(({ file_name, extension, file }) => ({
                        file_name,
                        extension,
                        file,
                    })),
                },
            });

            if (response?.data?.code === 200) {
                setTimeout(() => {
                    if (onDrawerClose) onDrawerClose();
                    onUpdateRefundListTrigger(new Date().getTime());
                }, 100);
            }
        } catch (err) {
            toast.error('Failed to update refund info.');
        } finally {
            setLoading(false);
        }
    }
    const savedPaymentProofs = canDownloadPaymentProof ? getSavedPaymentProofs() : [];
    let refundRequestAmount = refundData?.Amount;
    const countryLower = currentCountry?.toLowerCase();
    if (countryLower === 'om' || countryLower === 'ae' || countryLower === 'qa') {
        refundRequestAmount = refundData?.Amount * 9.6;
    } else if (countryLower === 'bh') {
        refundRequestAmount = refundData?.Amount * 9.74;
    }

    return (
        <>
            <CardHeader className="border-bottom py-3 px-4">
                <Row className="align-items-center">
                    <Col>
                        <CardTitle className="mb-0 fw-bold fs-16">Bank Account Information</CardTitle>
                    </Col>
                    <Col className="textAlignRight">
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => {
                                if (onDrawerClose) onDrawerClose();
                            }}
                            aria-label="Close"
                        ></button>
                    </Col>
                </Row>
            </CardHeader>
            <CardBody className="px-4 py-3" style={{ maxHeight: '800px', overflowY: 'auto' }}>
                {editMode && refundData.Status > 0 ? (
                    <></>
                ) : (
                    <Row>
                        {refundData.IbanNo && (<Col xs="12">
                            <Card>
                                <CardHeader className="border-bottom">
                                    <h5>Bank Account Information</h5>
                                </CardHeader>
                                <CardBody>
                                    <Row style={{ rowGap: '1rem' }}>
                                        <Col xs="6">
                                            <Label className="pb-2">Bank Name</Label>
                                            <span className="bg-light px-3 py-3 d-block rounded fw-bold fs-14">
                                                {refundData?.BankName}
                                            </span>
                                        </Col>
                                        <Col xs="6">
                                            <Label className="pb-2">Branch Name/Location</Label>
                                            <span className="bg-light px-3 py-3 d-block rounded fw-bold fs-14">
                                                {refundData?.BranchName}
                                            </span>
                                        </Col>
                                    </Row>
                                    <Row style={{ rowGap: '1rem' }} className='pt-2'>
                                        <Col xs="6">
                                            <Label className="pb-2">Account Holder’s Name</Label>
                                            <span className="bg-light px-3 py-3 d-block rounded fw-bold fs-14">
                                                {refundData?.AccountHolderName}
                                            </span>
                                        </Col>
                                        <Col xs="6">
                                            <Label className="pb-2">IBAN Number</Label>
                                            <span className="bg-light px-3 py-3 d-block rounded fw-bold fs-14">
                                                {refundData?.IbanNo}
                                            </span>
                                        </Col>

                                        <Col xs="6">
                                            <Label className="pb-2">
                                                SWIFT/BIC Code (if applicable):
                                            </Label>
                                            <span className="bg-light px-3 py-3 d-block rounded fw-bold fs-14">
                                                {refundData?.Swift}
                                            </span>
                                        </Col>
                                        <Col xs="6">
                                            <Label className="pb-2">
                                                Refund reference #:
                                            </Label>
                                            <span className="bg-light px-3 py-3 d-block rounded fw-bold fs-14">
                                                {refundData?.refundReference || '-'}
                                            </span>
                                        </Col>
                                    </Row>
                                    <Row style={{ rowGap: '1rem' }} className='pt-2'>
                                        {idFrontImage && canDownloadIdProof && (
                                            <Col xs="6" className="mb-3">
                                                <Label className="pb-2">ID Front</Label>
                                                <a
                                                    href={`data:image/jpeg;base64,${idFrontImage}`}
                                                    download="ID_Front.jpg"
                                                    className="d-block text-primary"
                                                >
                                                    Download 
                                                    <i className="bx bx-download download-icon"></i>
                                                </a>
                                            </Col>
                                        )}
                                        {idBackImage && canDownloadIdProof && (
                                            <Col xs="6" className="mb-3">
                                                <Label className="pb-2">ID Back</Label>
                                                <div className="d-flex align-items-center gap-3 flex-wrap">
                                                    <a
                                                        href={`data:image/jpeg;base64,${idBackImage}`}
                                                        download="ID_Back.jpg"
                                                        className="text-primary"
                                                    >
                                                        Download
                                                        <i className="bx bx-download download-icon"></i>
                                                    </a>
                                                    {paymentProofUrl && (
                                                        <a
                                                            href={paymentProofUrl}
                                                            download="payment_proof"
                                                            className="text-primary"
                                                        >
                                                            Payment Proof
                                                            <i className="bx bx-download download-icon"></i>
                                                        </a>
                                                    )}
                                                </div>
                                                {paymentProofZipUrl && paymentProofDownloadUrl && paymentProofZipUrl !== paymentProofDownloadUrl && (
                                                    <>
                                                        <Label className="pt-3 pb-2">Payment Proof ZIP</Label>
                                                        <a
                                                            href={paymentProofZipUrl}
                                                            download
                                                            className="d-block text-primary"
                                                        >
                                                            Download
                                                            <i className="bx bx-download download-icon"></i>
                                                        </a>
                                                    </>
                                                )}
                                            </Col>
                                        )}
                                        {!idBackImage && paymentProofZipUrl && (
                                            <Col xs="6" className="mb-3">
                                                <Label className="pb-2">Payment Proof ZIP</Label>
                                                <a
                                                    href={paymentProofZipUrl}
                                                    download
                                                    className="d-block text-primary"
                                                >
                                                    Download
                                                    <i className="bx bx-download download-icon"></i>
                                                </a>
                                            </Col>
                                        )}
                                        {savedPaymentProofs.map((proof, index) => (
                                            <Col xs="6" className="mb-3" key={proof.fileName + index}>
                                                <Label className="pb-2">
                                                    Payment Proof{savedPaymentProofs.length > 1 ? ` ${index + 1}` : ''}
                                                </Label>
                                                <a
                                                    href={proof.href}
                                                    download={proof.fileName}
                                                    className="d-block text-primary"
                                                >
                                                    Download
                                                    <i className="bx bx-download download-icon"></i>
                                                </a>
                                            </Col>
                                        ))}
                                        {refundData?.rejectedReason && (
                                            <Col xs="6" className="mb-3">
                                                <Label className="pb-2">Rejected Reason</Label>
                                                <span className="bg-light px-3 py-3 d-block rounded fw-bold fs-14 text-danger">
                                                    {refundData.rejectedReason}
                                                </span>
                                            </Col>
                                        )}
                                    </Row>

                                </CardBody>
                            </Card>
                        </Col>)}
                        {!editMode && (<Col xs="12" className="mt-4">
                            <Card>
                                <CardHeader className="border-bottom">
                                    <h5>Action History</h5>
                                </CardHeader>
                                <CardBody style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    <div className="table-responsive">
                                        <table className="table table-bordered table-striped">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '30%' }}>Created By</th>
                                                    <th style={{ width: '20%' }}>Status</th>
                                                    <th style={{ width: '45%' }}>Comment</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {activityLog.length > 0 ? (
                                                    activityLog.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.user_id == 1 ? '--' : item.user_name}<br /><span className="small-font">Date :{item.created_at}</span></td>
                                                            <td>{getStatusLabel(item.status)}</td>
                                                            <td>{item.comment}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan={4} className="text-center">History Not available</td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>)}

                    </Row>
                )}
                {editMode && (
                    <Card className="border shadow-sm mt-2">
                        <CardHeader className="border-bottom bg-light">
                            <h5 className="mb-0">Update Refund</h5>
                        </CardHeader>
                        <CardBody>
                    <Row style={{ rowGap: '1.25rem' }}>
                        <Col xs="6">
                            <Label className="pb-2">Refund Status</Label>
                            <select
                                className="form-select"
                                value={status}
                                onChange={(e) => {
                                    setStatus(e.target.value);
                                    if (e.target.value === '3') {
                                        setRefundReference('');
                                    }
                                    if (e.target.value !== '5') {
                                        setRejectedReason(''); // Reset rejected reason if not rejected
                                    }
                                }}
                            >
                                <option value="">Select status</option> {/* Default option */}
                                {statusOptions
                                    .filter((opt) => {
                                        //Super Admin
                                        if (UserRoleID === '1') {
                                            return true; // Show all options
                                        }
                                        //Finance Manager
                                        if (FINANCE_ROLE_ID.includes(UserRoleID) && refundData?.Status === '9') {
                                            return ['2', '5'].includes(opt.value); // Allow Approve and Rejected
                                        }
                                        //Finance Manager - only role allowed to act on a finance approved refund
                                        if (FINANCE_MANAGER_ROLE_ID.includes(UserRoleID) && refundData?.Status === '10') {
                                            return ['2', '5'].includes(opt.value); // Allow Approve and Rejected
                                        }
                                        //Audit - only role allowed to act on an audit approval pending refund
                                        if (AUDIT_ROLE_ID.includes(UserRoleID) && refundData?.Status === '20') {
                                            return ['3'].includes(opt.value); // Allow Completed
                                        }
                                        //Show room Manager
                                        if (UserRoleID === SHOW_ROOM_MANAGER_ROLE_ID && refundData?.Status === '8') {
                                            return ['2', '5'].includes(opt.value); // Allow Approve and Rejected
                                        }
                                        //Customer Service Manager
                                        if (UserRoleID === CUSTOMER_CARE_MANAGER_ROLE_ID && refundData?.Status === '7') {
                                            return ['2', '5'].includes(opt.value); // Allow Approve and Rejected
                                        }
                                        //Customer service Superviser
                                        if ((UserRoleID === CUSTOMER_CARE_SUPERVISOR_ROLE_ID || UserRoleID === STORE_CUSTOMER_CARE_SUPERVISOR_ROLE_ID) && (refundData?.Status === '11' || refundData?.Status === '13' || refundData?.Status === '15')) {
                                            return ['6'].includes(opt.value); // Allow Approve and Rejected
                                        }

                                        return false; // No options for other roles
                                    })
                                    .map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                            </select>
                        </Col>
                        {status === '5' && (FINANCE_ROLE_ID.includes(UserRoleID) || FINANCE_MANAGER_ROLE_ID.includes(UserRoleID)) && (
                            <Col xs="6">
                                <Label className="pb-2">Rejected Reason</Label>
                                <select
                                    className="form-select"
                                    value={rejectedReason}
                                    onChange={(e) => setRejectedReason(e.target.value)}
                                    required
                                >
                                    <option value="">Select reason</option>
                                    <option value="others">Others</option>
                                </select>
                            </Col>
                        )}
                        {status === '3' && (
                            <Col xs="6">
                                <Label className="pb-2">Refund Reference</Label>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={refundReference}
                                    onChange={(e) => setRefundReference(e.target.value)}
                                    placeholder="Enter refund reference"
                                />
                            </Col>
                        )}
                        <Col xs="12">
                            <Label className="pb-2">Remark</Label>
                            <textarea
                                className="form-control"
                                rows={3}
                                placeholder="Enter remark here..."
                                value={remark}
                                onChange={(e) => setRemark(e.target.value)}
                            />
                        </Col>
                        {isFinanceApprovalPendingStatus && (
                            <Col xs="12">
                                <Label className="pb-2">Payment Proof</Label>
                                <input
                                    type="file"
                                    className="form-control"
                                    accept=".pdf,.jpg,.jpeg,application/pdf,image/jpeg"
                                    multiple
                                    ref={paymentProofInputRef}
                                    onChange={handlePaymentProofChange}
                                />
                                {paymentProofFiles.length > 0 && (
                                    <>
                                        <span className="form-text">
                                            {paymentProofFiles.length} file(s) selected
                                        </span>
                                        <ul className="list-unstyled mb-0 mt-1">
                                            {paymentProofFiles.map((proof) => (
                                                <li key={proof.id} className="d-flex align-items-center">
                                                    <span className="text-success">{proof.file_name}</span>
                                                    <button
                                                        type="button"
                                                        className="btn btn-link btn-sm text-danger p-0 ms-2"
                                                        onClick={() => handleRemovePaymentProof(proof.id)}
                                                    >
                                                        Remove
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                            </Col>
                        )}
                    </Row>
                        </CardBody>
                    </Card>
                )}
            </CardBody>
            {editMode && (
                <div className="d-flex justify-content-start border-top px-4 py-3">
                    <Button color="primary" className="px-4" onClick={handleUpdate} disabled={loading}>
                        {loading ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        ) : (
                            'Update'
                        )}
                    </Button>
                </div>
            )}
        </>
    );
};

// Optimized repetitive code by creating a helper function for status labels
function getStatusLabel(status) {
    switch (status) {
        case 0:
        case '0':
            return 'Pending';
        case 1:
        case '1':
            return 'Approval Pending';
        case 2:
        case '2':
            return 'Approved';
        case 3:
        case '3':
            return 'Completed';
        case 4:
        case '4':
            return 'Clarification Required';
        case 5:
        case '5':
            return 'Rejected';
        case 9:
        case '9':
            return 'Finance Approval Pending';
        case 10:
        case '10':
            return 'Finance Approved';
        case 11:
        case '11':
            return 'Finance Rejected';
        case 20:
        case '20':
            return 'Audit Approval Pending';
        case 12:
        case '12':
            return 'Show room manager Approved';
        case 13:
        case '13':
            return 'Show room manager Rejected';
        case 14:
        case '14':
            return 'Customer Service manager Approved';
        case 14:
        case '14':
            return 'Customer Service manager Rejected';
        default:
            return status;
    }
}

export default BankInfo;
